<script setup lang="ts">
/**
 * Refuses to render a screen that is too narrow for it, and offers a way out.
 *
 * DESIGN.md section 12: `ADM-*` grids are "explicitly **not supported** below
 * 768" — "CSV export and bulk operations on a phone are a false promise, so reuse
 * the deep-link pattern".
 *
 * "Deep-link out" on web means handing the user the URL to open on a machine that
 * can render it, so the copy-link control is the substance of this component
 * rather than decoration. The alternative — a five-column grid with horizontal
 * scroll on a 360px screen — is the broken render the rule exists to forbid.
 */

import { computed, ref } from 'vue'

import MpButton from '@/components/base/MpButton.vue'
import MpIcon from '@/components/base/MpIcon.vue'
import { useViewport } from '@/lib/viewport'
import { useUiStore } from '@/stores/ui'

const props = defineProps<{ minWidth: number; screenTitle: string }>()

const viewport = useViewport()
const ui = useUiStore()
const supported = computed(() => viewport.width.value >= props.minWidth)

const href = ref(typeof window === 'undefined' ? '' : window.location.href)

async function copyLink (): Promise<void> {
  try {
    await navigator.clipboard.writeText(href.value)
    ui.succeeded('Link copied', 'Open it on a desktop browser to use this screen.')
  } catch {
    // Clipboard access is refused in plenty of legitimate configurations, so the
    // URL is on screen as text too and this is a convenience, not the mechanism.
    ui.warned('Could not copy the link', 'The address is shown below — copy it manually.')
  }
}
</script>

<template>
  <slot v-if="supported" />

  <div
    v-else
    class="mp-gate"
  >
    <span class="mp-gate__mark"><MpIcon name="external" /></span>
    <h2 class="mp-gate__headline">
      {{ props.screenTitle }} needs a wider screen
    </h2>
    <p class="mp-gate__body">
      This screen is a dense grid with bulk actions and export. It is not supported below
      <span class="mp-num">{{ props.minWidth }}</span>px, and rendering it here would give you a
      table you cannot read rather than one you can work in.
    </p>
    <p class="mp-gate__link">
      <code>{{ href }}</code>
    </p>
    <div class="mp-gate__actions">
      <MpButton
        variant="primary"
        @click="copyLink"
      >
        Copy the link for desktop
      </MpButton>
      <MpButton
        variant="secondary"
        @click="$router.push('/home')"
      >
        Go to home
      </MpButton>
    </div>
  </div>
</template>

<style scoped>
.mp-gate {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  max-inline-size: 34rem;
  padding: var(--card-pad);
  background: var(--surface-raised);
  border: 1px solid var(--border-control);
  border-radius: var(--radius-card);
  /* Prose on the default scale even though ADM-* screens are compact surfaces:
     this is an explanation, not a grid. DESIGN.md section 6. */
  font-size: var(--font-size-body);
}

.mp-gate__mark {
  color: var(--accent);
}

.mp-gate__headline {
  font-size: var(--font-size-h3);
}

.mp-gate__body {
  color: var(--text-secondary);
  line-height: 1.6;
}

.mp-gate__link {
  padding: var(--space-2) var(--space-3);
  background: var(--surface-sunken);
  border-radius: var(--radius-control);
  font-size: var(--font-size-caption);
  overflow-wrap: anywhere;
}

.mp-gate__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}
</style>
